import { useCallback, useEffect, useRef, useState } from "react";
import type { Klausel, Werkzeugkasten } from "../hooks/useVertragstext";
import { useEntwurfMelden } from "../hooks/useEntwuerfe";
import { SCHALTER_LABEL, SCHALTER_WERTE } from "../hooks/useVertragstext";

/**
 * Ein Absatz oder ein platzierter Baustein — die kleinste Einheit des
 * Vertragstextes.
 *
 * Die Nummer links (`(3)`) ist **Anzeige, kein Eingabefeld**. Sie entsteht aus
 * der Position und wandert mit, wenn darüber etwas gelöscht oder verschoben
 * wird. Das ist der Grund, aus dem der Editor überhaupt so gebaut ist: eine
 * getippte Nummer überlebt keine Umsortierung, und niemand merkt es.
 */
export function KlauselKarte({
  klausel, nummer, marken, werkzeug, schreibbar,
  onAendern, onLoeschen, onHoch, onRunter,
}: {
  klausel: Klausel;
  nummer: number | null;
  marken: { schluessel: string; zeigt: string }[];
  werkzeug?: Werkzeugkasten;
  schreibbar: boolean;
  onAendern: (patch: Partial<Klausel>) => void;
  onLoeschen: () => void;
  onHoch?: () => void;
  onRunter?: () => void;
}) {
  const [text, setText] = useState(klausel.text);
  const [offen, setOffen] = useState(false);
  const feld = useRef<HTMLTextAreaElement>(null);

  // Kommt der Absatz vom Server neu (Umsortieren, Verwerfen), muss das Feld
  // mitziehen — sonst zeigt es weiter den Stand von vorhin.
  useEffect(() => setText(klausel.text), [klausel.id, klausel.text]);

  const doppelt = (werkzeug?.doppelte_zahlen || [])
    .filter(z => text.includes(z.muster));

  /** An der Cursorposition einfügen, nicht am Ende: sonst muss man jeden
   *  Platzhalter von Hand an seinen Platz schieben. */
  function einfuegen(schnipsel: string) {
    const el = feld.current;
    if (!el) return;
    const a = el.selectionStart ?? text.length;
    const b = el.selectionEnd ?? text.length;
    const neu = text.slice(0, a) + schnipsel + text.slice(b);
    setText(neu);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + schnipsel.length, a + schnipsel.length);
    });
  }

  // Was zuletzt an den Server ging. Verlassen des Feldes (onBlur) und der Knopf
  // „Speichern" feuern oft direkt nacheinander — ein Klick auf den Knopf nimmt
  // dem Feld zuerst den Fokus. Ohne diese Sperre ginge derselbe Text zweimal raus.
  const gesendet = useRef<string | null>(null);
  useEffect(() => { gesendet.current = null; }, [klausel.id, klausel.text]);

  function sichern() {
    if (text === klausel.text || gesendet.current === text) return;
    gesendet.current = text;
    onAendern({ text });
  }

  // Beim Knopf „Speichern" oben mitzählen, solange hier etwas offen ist.
  const sichernFuerKnopf = useCallback(() => sichern(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, klausel.id, klausel.text]);
  useEntwurfMelden(`klausel:${klausel.id}`,
                   klausel.art === "absatz" && schreibbar && text !== klausel.text,
                   sichernFuerKnopf);

  if (klausel.art === "baustein") {
    const titel = werkzeug?.bausteine.find(b => b.name === klausel.baustein)?.titel;
    return (
      <div className="vt-klausel is-baustein">
        <div className="vt-klausel-nr">▦</div>
        <div className="vt-klausel-haupt">
          <div className="vt-baustein-titel">{titel || klausel.baustein}</div>
          <div className="vt-hint">
            Gerechnete Tabelle — Inhalt kommt aus Katalog und Kalkulation. Hier lässt sie
            sich verschieben und entfernen, nicht bearbeiten.
          </div>
          <BedingungWahl klausel={klausel} schreibbar={schreibbar} onAendern={onAendern} />
        </div>
        {schreibbar && (
          <Werkzeuge onHoch={onHoch} onRunter={onRunter} onLoeschen={onLoeschen} />
        )}
      </div>
    );
  }

  return (
    <div className="vt-klausel">
      <div className="vt-klausel-nr" title="Nummer entsteht aus der Reihenfolge">
        {nummer ? `(${nummer})` : "·"}
      </div>
      <div className="vt-klausel-haupt">
        <textarea
          ref={feld}
          className="vt-text"
          value={text}
          rows={Math.max(3, Math.ceil(text.length / 90))}
          readOnly={!schreibbar}
          onChange={e => setText(e.target.value)}
          onBlur={sichern}
        />

        {doppelt.length > 0 && (
          <div className="vt-warnung">
            {doppelt.map(z => (
              <div key={z.muster}>
                <strong>„{z.muster}“</strong> ist auch im Programm hinterlegt ({z.hinweis}).
                Änderst du den Wert nur hier, rechnet das Programm weiter mit dem alten —
                dann muss es mit angepasst werden.
              </div>
            ))}
          </div>
        )}

        {schreibbar && (
          <div className="vt-palette">
            <button className="vt-chip" onClick={() => einfuegen("**fett**")}>**fett**</button>
            <details className="vt-drop">
              <summary className="vt-chip">Platzhalter</summary>
              <div className="vt-drop-liste">
                {(werkzeug?.platzhalter || []).map(p => (
                  <button key={p.name} className="vt-drop-zeile"
                          onClick={() => einfuegen(`{{${p.name}}}`)}>
                    <span className="vt-drop-name">{`{{${p.name}}}`}</span>
                    <span className="vt-drop-bsp">{p.beispiel || "—"}</span>
                  </button>
                ))}
              </div>
            </details>
            <details className="vt-drop">
              <summary className="vt-chip">Verweis</summary>
              <div className="vt-drop-liste">
                {marken.length === 0 && (
                  <div className="vt-drop-leer">
                    Noch keine Marke vergeben. Eine Klausel wird verweisbar, sobald sie
                    unten eine Marke bekommt.
                  </div>
                )}
                {marken.map(m => (
                  <div key={m.schluessel} className="vt-drop-paar">
                    <button className="vt-drop-zeile"
                            onClick={() => einfuegen(`{{§${m.schluessel}}}`)}>
                      <span className="vt-drop-name">{m.schluessel}</span>
                      <span className="vt-drop-bsp">{m.zeigt.split(" Abs.")[0]}</span>
                    </button>
                    {m.zeigt.includes("Abs.") && (
                      <button className="vt-drop-zeile"
                              onClick={() => einfuegen(`{{§${m.schluessel}/abs}}`)}>
                        <span className="vt-drop-name">{m.schluessel}/abs</span>
                        <span className="vt-drop-bsp">{m.zeigt}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </details>
            <button className="vt-chip is-quiet" onClick={() => setOffen(v => !v)}>
              {offen ? "Weniger" : "Marke, Gültigkeit, Stil"}
            </button>
          </div>
        )}

        {offen && schreibbar && (
          <div className="vt-detail">
            <label className="pf-field">
              <span>Verweismarke (leer = nicht verweisbar)</span>
              <input
                className="pf-input is-tight" defaultValue={klausel.schluessel || ""}
                placeholder="z. B. preisanpassung"
                onBlur={e => {
                  const wert = e.target.value.trim();
                  if (wert !== (klausel.schluessel || "")) onAendern({ schluessel: wert });
                }}
              />
            </label>
            <BedingungWahl klausel={klausel} schreibbar={schreibbar} onAendern={onAendern} />
            {klausel.anhang && (
              <label className="pf-field">
                <span>Schriftgröße</span>
                <select
                  className="pf-input is-tight" defaultValue={klausel.stil || ""}
                  onChange={e => onAendern({ stil: e.target.value })}
                >
                  <option value="">normal</option>
                  <option value="klein">klein (Rahmentext)</option>
                </select>
              </label>
            )}
          </div>
        )}
      </div>
      {schreibbar && <Werkzeuge onHoch={onHoch} onRunter={onRunter} onLoeschen={onLoeschen} />}
    </div>
  );
}

function Werkzeuge({ onHoch, onRunter, onLoeschen }: {
  onHoch?: () => void; onRunter?: () => void; onLoeschen: () => void;
}) {
  return (
    <div className="vt-klausel-tools">
      <button className="pf-iconbtn" onClick={onHoch} disabled={!onHoch} title="Nach oben">↑</button>
      <button className="pf-iconbtn" onClick={onRunter} disabled={!onRunter} title="Nach unten">↓</button>
      <button className="pf-iconbtn is-weg" onClick={onLoeschen} title="Entfernen">✕</button>
    </div>
  );
}

/**
 * „Dieser Absatz gilt für:" — feste Schalter, keine Ausdrücke.
 *
 * Bewusst kein Freitextfeld für Bedingungen. Sobald hier Ausdrücke stünden,
 * wäre der Editor eine zweite Programmiersprache — mit dem Unterschied, dass
 * niemand sie testet und ein Tippfehler direkt in einem Vertrag landet.
 */
function BedingungWahl({ klausel, schreibbar, onAendern }: {
  klausel: Klausel; schreibbar: boolean;
  onAendern: (patch: Partial<Klausel>) => void;
}) {
  const bedingung = klausel.bedingung || {};
  const eintraege = Object.entries(bedingung);

  function setzen(name: string, wert: string) {
    const neu = { ...bedingung };
    if (wert === "") delete neu[name];
    else if (wert === "ja") neu[name] = true;
    else if (wert === "nein") neu[name] = false;
    else neu[name] = wert;
    onAendern({ bedingung: Object.keys(neu).length ? neu : null });
  }

  return (
    <div className="vt-bedingung">
      <span className="vt-bedingung-label">Gilt:</span>
      {eintraege.length === 0 && <span className="vt-bedingung-immer">immer</span>}
      {eintraege.map(([name, wert]) => (
        <span key={name} className="vt-bedingung-chip">
          {SCHALTER_LABEL[name] || name} = {wert === true ? "ja" : wert === false ? "nein" : wert}
          {schreibbar && (
            <button className="vt-chip-x" title="Bedingung entfernen"
                    onClick={() => setzen(name, "")}>✕</button>
          )}
        </span>
      ))}
      {schreibbar && (
        <select
          className="vt-bedingung-neu" value=""
          onChange={e => {
            const [name, wert] = e.target.value.split("=");
            if (name) setzen(name, wert);
          }}
        >
          <option value="">＋ Bedingung…</option>
          {Object.keys(SCHALTER_LABEL)
            .filter(n => !(n in bedingung))
            .map(n => (
              <optgroup key={n} label={SCHALTER_LABEL[n]}>
                {(SCHALTER_WERTE[n] || ["ja", "nein"]).map(w => (
                  <option key={w} value={`${n}=${w}`}>{SCHALTER_LABEL[n]}: {w}</option>
                ))}
              </optgroup>
            ))}
        </select>
      )}
    </div>
  );
}
